import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {useDispatch} from 'react-redux';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteServiceOrderReturnMutation} from '@dms/api/warehouse';
import {metadaWorkshopServiceOrderApi} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError, logError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback, EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

interface ServiceCaseOrderReturnsDataGridProps {
  maximization?: EagGridMaximization;
  orderId: string;
}

export function ServiceCaseOrderReturnsDataGrid(props: ServiceCaseOrderReturnsDataGridProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [deleteServiceOrderReturn] = useDeleteServiceOrderReturnMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {serviceOrderId: props.orderId}]);

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const selectedRowId = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(selectedRowId)) {
      return logError('Selected row id is undefined');
    }

    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        navigate(
          composePath(warehouseRoutes.serviceOrderReturnsDetailOverview, {
            params: {
              id: selectedRowId,
            },
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('delete', () => {
        openDeleteDialog({
          text: i18n.t('entity.warehouse.actions.deleteEntity'),
          onConfirm: () =>
            deleteServiceOrderReturn({body: {serviceOrderReturnId: [selectedRowId]}})
              .unwrap()
              .then(() =>
                dispatch(
                  metadaWorkshopServiceOrderApi.util.invalidateTags([
                    {type: 'partMovementsCount', id: props.orderId},
                  ])
                )
              )
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <DmsDataGrid
      maximization={props.maximization}
      autoHeight
      gridCode="service-case-order-returns"
      queryModifier={queryModifier}
      data-testid="serviceOrderReturns"
      actionCallback={actionCallback}
    />
  );
}
